<template>
  <div class="well">
    <h4>{{ isEdit ? 'Edit' : 'Setup' }} Autoprovisioning Configuration</h4>
    <div v-if="loading" class="alert alert-info">
      <p>Processing...</p>
    </div>
    <div v-if="error" class="alert alert-danger">
      <p>{{ error }}</p>
    </div>
    <form @submit.prevent="saveConfig">
      <div class="form-group">
        <label class="control-label">Roster Service *</label>
        <div>
          <label class="radio-inline radio-option-first">
            <input
              id="roster-service-clever"
              type="radio"
              name="roster-service"
              :value="ROSTER_SERVICE_CLEVER"
              v-model="lambdaInput.roster_service"
              required
            />
            Clever
          </label>
          <label class="radio-inline">
            <input
              type="radio"
              name="roster-service"
              :value="ROSTER_SERVICE_ONEROSTER"
              v-model="lambdaInput.roster_service"
            />
            OneRoster
          </label>
          <label class="radio-inline">
            <input
              type="radio"
              name="roster-service"
              :value="ROSTER_SERVICE_S3CSV"
              v-model="lambdaInput.roster_service"
            />
            SFTP
          </label>
        </div>
      </div>

      <!-- Clever specific configuration -->
      <div v-if="lambdaInput.roster_service === ROSTER_SERVICE_CLEVER" class="panel panel-default">
        <div class="panel-heading">Clever Configuration</div>
        <div class="panel-body">
          <div class="form-group">
            <label for="clever-bearer-token">Clever Bearer Token *</label>
            <input
              id="clever-bearer-token"
              v-model="lambdaInput.clever_config.bearer_token"
              type="text"
              class="form-control"
              required
              maxlength="40"
              minlength="40"
            />
            <p class="help-block">The bearer token should be 40 characters long.</p>
          </div>
          <div class="form-group">
            <label class="control-label">District Admins</label>
            <i class="fa fa-info-circle" id="district_admins_tooltip" />
            <tooltip
              text="If the district is sharing admins via a sharing rule, select the default option."
              target="#district_admins_tooltip"
            />
            <div class="radio">
              <label>
                <input type="radio" value="none" v-model="districtAdminsInclusionType" />
                {{ DISTRICT_ADMINS_LABELS.none }}
              </label>
            </div>
            <div class="radio">
              <label>
                <input type="radio" value="all" v-model="districtAdminsInclusionType" />
                {{ DISTRICT_ADMINS_LABELS.all }}
              </label>
            </div>
            <div class="radio">
              <label>
                <input type="radio" value="specific" v-model="districtAdminsInclusionType" />
                {{ DISTRICT_ADMINS_LABELS.specific }}
              </label>
            </div>
          </div>

          <div class="form-group" v-if="districtAdminsInclusionType === 'specific'">
            <label for="district-admins">District Admins to Include (one email per line)</label>
            <textarea
              id="district-admins"
              v-model="districtAdminsText"
              class="form-control"
              rows="5"
              placeholder="Enter one email address per line"
            />
            <p class="help-block">
              Specify district admin emails to include in the import. This would be admins not
              configured in Clever sharing rules.
            </p>
          </div>
        </div>
      </div>

      <!-- CSV/SFTP specific configuration -->
      <div v-if="lambdaInput.roster_service === ROSTER_SERVICE_S3CSV" class="panel panel-default">
        <div class="panel-heading">SFTP Autoprovisioning (CSV) Configuration</div>
        <div class="panel-body">
          <div class="form-group">
            <label for="s3-input-directory">S3 CSV Input Directory *</label>
            <i class="fa fa-info-circle" id="s3_input_directory_tooltip" />
            <tooltip
              text="The district's S3 directory path where CSV files are uploaded via SFTP."
              target="#s3_input_directory_tooltip"
            />
            <input
              id="s3-input-directory"
              v-model="lambdaInput.s3_csv_config.s3_prefix"
              type="text"
              class="form-control"
              required
              placeholder="e.g., districtdirectory"
            />
          </div>

          <div class="form-group">
            <label for="s3-filename-prefix">File Name Prefix (optional)</label>
            <i class="fa fa-info-circle" id="s3_filename_prefix_tooltip" />
            <tooltip
              text="If your CSV files have a consistent prefix, enter it here. This helps the system filter the files to process. For example, if your files are named 'Full_users.csv', enter 'Full_'."
              target="#s3_filename_prefix_tooltip"
            />
            <input
              id="s3-filename-prefix"
              v-model="lambdaInput.s3_csv_config.filename_prefix"
              type="text"
              class="form-control"
              placeholder="e.g., Full_"
            />
          </div>

          <div class="form-group">
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="customizeFieldMappings" />
                <strong>Customize CSV field mappings</strong>
              </label>
            </div>
            <p class="help-block">
              If custom mappings aren't provided, the CSV must follow the
              <a
                href="https://docs.google.com/spreadsheets/d/1zdTr1k_WpdT_Jhk-a_XGkeLR2_rjcIEmbye5ouHr7aA/edit?gid=0#gid=0"
                rel="noopener noreferrer"
                target="_blank"
                >standard template format</a
              >.
            </p>
          </div>

          <div v-if="customizeFieldMappings">
            <h5>CSV Field Mappings</h5>
            <p class="help-block">
              Map the column names from the CSV file to the required fields. Column names are
              case-sensitive.
            </p>

            <div class="form-group">
              <label for="csv-name-field">Name Field *</label>
              <input
                id="csv-name-field"
                v-model="lambdaInput.s3_csv_options.field_mappings.name_field"
                type="text"
                class="form-control"
                :required="customizeFieldMappings"
              />
            </div>

            <div class="form-group">
              <label for="csv-email-field">Email Field *</label>
              <input
                id="csv-email-field"
                v-model="lambdaInput.s3_csv_options.field_mappings.email_field"
                type="text"
                class="form-control"
                :required="customizeFieldMappings"
              />
            </div>

            <div class="form-group">
              <label for="csv-title-field">Title Field *</label>
              <input
                id="csv-title-field"
                v-model="lambdaInput.s3_csv_options.field_mappings.title_field"
                type="text"
                class="form-control"
                :required="customizeFieldMappings"
              />
            </div>

            <div class="form-group">
              <label for="csv-school-field">School Field *</label>
              <input
                id="csv-school-field"
                v-model="lambdaInput.s3_csv_options.field_mappings.school_field"
                type="text"
                class="form-control"
                :required="customizeFieldMappings"
              />
            </div>

            <div class="form-group">
              <label for="csv-external-id-field">External ID Field (optional)</label>
              <input
                id="csv-external-id-field"
                v-model="lambdaInput.s3_csv_options.field_mappings.external_id_field"
                type="text"
                class="form-control"
              />
            </div>
          </div>

          <div class="form-group">
            <p class="control-label">SFTP User Configuration (aka Transfer Family)</p>
            <p class="help-block">
              This how the partner will have access to send us files via SFTP.
            </p>
            <button
              type="button"
              class="btn btn-default btn-sm"
              @click="openTransferFamilyUserModal"
            >
              Manage SFTP User
            </button>
            <button
              v-if="transferFamilyUser"
              type="button"
              class="btn btn-danger btn-sm"
              style="margin-left: 8px"
              @click="deleteTransferFamilyUser"
            >
              Delete SFTP User
            </button>
          </div>
        </div>
      </div>

      <!-- Transfer Family User Modal -->
      <modal
        :value="showTransferFamilyModal"
        title="SFTP User Configuration"
        @hide="showTransferFamilyModal = false"
        @input="showTransferFamilyModal = $event"
      >
        <transfer-family-user-form
          v-if="transferFamilyUserFormModel"
          :value="transferFamilyUserFormModel"
          :mode="transferFamilyUserMode"
          @submit="handleTransferFamilyUserSubmit"
          @cancel="showTransferFamilyModal = false"
        />
      </modal>

      <!-- OneRoster specific configuration -->
      <div
        v-if="lambdaInput.roster_service === ROSTER_SERVICE_ONEROSTER"
        class="panel panel-default"
      >
        <div class="panel-heading">OneRoster Configuration</div>
        <div class="panel-body">
          <div class="form-group">
            <label for="oneroster-client-id">OneRoster Client ID *</label>
            <input
              id="oneroster-client-id"
              v-model="lambdaInput.provider_config.client_id"
              type="text"
              class="form-control"
              required
              min-length="10"
              max-length="40"
            />
          </div>
          <div class="form-group">
            <label for="oneroster-client-secret">OneRoster Client Secret *</label>
            <input
              id="oneroster-client-secret"
              v-model="lambdaInput.provider_config.client_secret"
              type="text"
              class="form-control"
              required
              min-length="10"
              max-length="40"
            />
          </div>
          <div class="form-group">
            <label for="oneroster-endpoint">OneRoster Endpoint URL *</label>
            <input
              id="oneroster-endpoint"
              v-model="lambdaInput.provider_config.endpoint"
              type="text"
              class="form-control"
              required
              placeholder="https://demo-v2.rosterserver.com"
            />
            <p class="help-block">Enter the full URL including https://</p>
          </div>
          <div class="form-group">
            <label for="oneroster-title-field">OneRoster Title Field (optional)</label>
            <i class="fa fa-info-circle" id="oneroster_title_tooltip" />
            <tooltip
              text="If a domain has more specific job titles, they can share it as a metadata field. Insert the name of the metadata field here (with correct casing)."
              target="#oneroster_title_tooltip"
            />
            <input
              id="oneroster-title-field"
              v-model="lambdaInput.oneroster_options.metadata_title_field"
              type="text"
              class="form-control"
            />
          </div>
          <div class="form-group">
            <label for="oneroster-unique-id">OneRoster Unique ID</label>
            <i class="fa fa-info-circle" id="oneroster_unique_id_tooltip" />
            <tooltip
              text="Select the unique identifier for users. 'sourcedId' is the default and most commonly used."
              target="#oneroster_unique_id_tooltip"
            />
            <select
              id="oneroster-unique-id"
              v-model="lambdaInput.oneroster_options.unique_id_field"
              class="form-control"
              required
            >
              <option value="sourcedId">sourcedId</option>
              <option value="email">email</option>
            </select>
          </div>
          <div class="form-group">
            <label for="oneroster-api-version">OneRoster API Version</label>
            <i class="fa fa-info-circle" id="oneroster_api_version_tooltip" />
            <tooltip
              text="Select the OneRoster API version to use. 'v1.2' is the default and most commonly used."
              target="#oneroster_api_version_tooltip"
            />
            <select
              id="oneroster-api-version"
              v-model="lambdaInput.provider_config.api_path_prefix"
              class="form-control"
              required
            >
              <option :value="oneRosterApiV2">{{
                getOneRosterApiVersionDisplay(oneRosterApiV2)
              }}</option>
              <option :value="oneRosterApiV1">{{
                getOneRosterApiVersionDisplay(oneRosterApiV1)
              }}</option>
            </select>
          </div>
        </div>
      </div>

      <h4>Import Options</h4>
      <div class="panel panel-default">
        <div class="panel-body">
          <div class="form-group">
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="autocommit" />
                <strong>Auto-commit Import</strong>
              </label>
              <p class="help-block">
                When enabled, imported user changes will automatically be saved. When disabled,
                changes will need to be manually reviewed and committed.
              </p>
            </div>
          </div>

          <div class="form-group">
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="deactivateMissing" />
                <strong>Auto-deactivate users</strong>
              </label>
              <p class="help-block">
                When enabled, this will automatically set the end date to the day of the import for
                all missing users.
              </p>
            </div>
          </div>

          <div class="form-group">
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="autoupdateTitles" />
                <strong>Auto-update titles</strong>
              </label>
              <p class="help-block">
                When enabled, imported users will receive titles from OneRoster/Clever. When
                disabled, users will retain local titles.
              </p>
            </div>
          </div>
          <div class="form-group">
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="two_kit" />
                <strong>Two Kit Import</strong>
              </label>
              <p class="help-block">
                When enabled, then all users in this import will receive Pathways licenses as well
                as Toolkit
              </p>
            </div>
          </div>
          <div class="form-group">
            <label for="csv-upload-prefix">S3 CSV Output Directory</label>
            <p>
              <code>{{ lambdaInput.csv_upload_s3_prefix }}</code>
            </p>
            <p class="help-block">
              This prefix is automatically generated based on your domain information.
            </p>
          </div>
        </div>
      </div>

      <!-- Validation Errors -->
      <div v-if="formValidationErrors.length > 0" class="alert alert-warning">
        <h5>Please fix the following issues:</h5>
        <ul>
          <li v-for="(error, index) in formValidationErrors" :key="index">{{ error }}</li>
        </ul>
      </div>

      <div class="form-group">
        <button type="submit" class="btn btn-primary" :disabled="loading || !isFormValid">
          {{ isEdit ? 'Update' : 'Save' }} Configuration
        </button>
        <button type="button" class="btn btn-default" @click="cancel">Cancel</button>
      </div>
    </form>
  </div>
</template>

<script>
import { Tooltip, Modal } from 'uiv'
import TransferFamilyUserForm from './TransferFamilyUserForm.vue'
import { ROSTER_SERVICE_CLEVER, ROSTER_SERVICE_ONEROSTER, ROSTER_SERVICE_S3CSV } from './constants'
import {
  getDistrictAdminsInclusionType,
  getOneRosterApiVersionDisplay,
  DISTRICT_ADMINS_LABELS
} from './utils/displayHelpers'

const oneRosterApiV1 = 'ims/oneroster/v1p1'
const oneRosterApiV2 = 'ims/oneroster/rostering/v1p2'

const EMPTY_FIELD_MAPPINGS = Object.freeze({
  name_field: '',
  email_field: '',
  title_field: '',
  school_field: '',
  external_id_field: ''
})

export default {
  components: { Tooltip, Modal, TransferFamilyUserForm },
  name: 'AutoprovisioningForm',
  props: {
    domainUuid: {
      type: String,
      required: true
    },
    domainName: {
      type: String,
      required: true
    },
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      deactivateMissing: false,
      autocommit: true,
      autoupdateTitles: true,
      two_kit: false,
      districtAdminsText: '',
      districtAdminsInclusionType: 'none', // 'none', 'all', or 'specific'
      customizeFieldMappings: false,
      oneRosterApiV1,
      oneRosterApiV2,
      DISTRICT_ADMINS_LABELS,
      lambdaInput: {
        roster_service: '',
        clever_config: {
          district_name: '',
          bearer_token: '',
          endpoint: 'https://api.clever.com/v3.0'
        },
        clever_options: {
          include_district_admins: false,
          district_admin_emails_to_include: []
        },
        provider_config: {
          provider_name: '',
          endpoint: '',
          client_id: '',
          client_secret: '',
          api_path_prefix: oneRosterApiV2
        },
        oneroster_options: {
          unique_id_field: 'sourcedId',
          metadata_title_field: ''
        },
        s3_csv_config: {
          domain_name: '',
          s3_bucket: '',
          s3_prefix: '',
          filename_prefix: ''
        },
        s3_csv_options: {
          field_mappings: { ...EMPTY_FIELD_MAPPINGS }
        },
        import_options: {
          autocommit: true,
          deactivate_missing: false,
          autoupdate_titles: true
        },
        csv_upload_s3_prefix: '',
        import_domain_uuid: ''
      },
      transferFamilyUser: null,
      showTransferFamilyModal: false,
      transferFamilyUserMode: 'create',
      transferFamilyUserFormModel: null
    }
  },
  computed: {
    isEdit() {
      return !!this.config
    },
    isFormValid() {
      return this.formValidationErrors.length === 0
    },
    formValidationErrors() {
      const errors = []

      this.addRosterServiceErrors(errors)
      this.addCleverErrors(errors)
      this.addOneRosterErrors(errors)
      this.addS3CsvErrors(errors)

      return errors
    }
  },
  watch: {
    deactivateMissing(newVal) {
      this.lambdaInput.import_options.deactivate_missing = newVal
    },
    autocommit(newVal) {
      this.lambdaInput.import_options.autocommit = newVal
    },
    autoupdateTitles(newVal) {
      this.lambdaInput.import_options.autoupdate_titles = newVal
    },
    two_kit(newVal) {
      this.lambdaInput.import_options.two_kit = newVal
    },
    districtAdminsText(newVal) {
      // Split the text by newlines and filter out empty lines
      const emails = newVal
        .split('\n')
        .map(email => email.trim())
        .filter(email => email.length > 0)

      this.lambdaInput.clever_options.district_admin_emails_to_include = emails
    },
    districtAdminsInclusionType(newVal) {
      if (newVal === 'all') {
        this.lambdaInput.clever_options.include_district_admins = true
        this.lambdaInput.clever_options.district_admin_emails_to_include = []
        this.districtAdminsText = ''
      } else {
        this.lambdaInput.clever_options.include_district_admins = false
        if (newVal === 'none') {
          this.lambdaInput.clever_options.district_admin_emails_to_include = []
          this.districtAdminsText = ''
        }
      }
    }
  },
  methods: {
    getOneRosterApiVersionDisplay,
    addRosterServiceErrors(errors) {
      if (!this.lambdaInput.roster_service) {
        errors.push('Please select a roster service')
      }
    },

    addCleverErrors(errors) {
      if (this.lambdaInput.roster_service === ROSTER_SERVICE_CLEVER) {
        if (!this.lambdaInput.clever_config.bearer_token) {
          errors.push('Clever bearer token is required')
        } else if (this.lambdaInput.clever_config.bearer_token.length !== 40) {
          errors.push('Clever bearer token must be 40 characters long')
        }
      }
    },

    addS3CsvErrors(errors) {
      if (this.lambdaInput.roster_service === ROSTER_SERVICE_S3CSV) {
        const s3Prefix = this.lambdaInput.s3_csv_config.s3_prefix || ''
        const normalizedPrefix = this.normalizeS3Prefix(s3Prefix)

        if (!normalizedPrefix) {
          errors.push('Valid S3 Input Directory is required')
        }

        if (this.customizeFieldMappings) {
          const nameField = this.lambdaInput.s3_csv_options.field_mappings.name_field || ''
          const emailField = this.lambdaInput.s3_csv_options.field_mappings.email_field || ''
          const titleField = this.lambdaInput.s3_csv_options.field_mappings.title_field || ''
          const schoolField = this.lambdaInput.s3_csv_options.field_mappings.school_field || ''

          if (!nameField.trim()) {
            errors.push('CSV Name field mapping is required')
          }
          if (!emailField.trim()) {
            errors.push('CSV Email field mapping is required')
          }
          if (!titleField.trim()) {
            errors.push('CSV Title field mapping is required')
          }
          if (!schoolField.trim()) {
            errors.push('CSV School field mapping is required')
          }
        }
      }
    },

    addOneRosterErrors(errors) {
      if (this.lambdaInput.roster_service === ROSTER_SERVICE_ONEROSTER) {
        const clientId = this.lambdaInput.provider_config.client_id || ''
        const clientSecret = this.lambdaInput.provider_config.client_secret || ''
        const endpoint = this.lambdaInput.provider_config.endpoint || ''

        if (!clientId.trim()) {
          errors.push('OneRoster Client ID is required')
        }
        if (!clientSecret.trim()) {
          errors.push('OneRoster Client Secret is required')
        }
        if (!endpoint.trim()) {
          errors.push('OneRoster Endpoint URL is required')
        } else if (!this.isValidUrl(endpoint)) {
          errors.push('OneRoster Endpoint URL must be a valid URL')
        }
        if (!this.lambdaInput.oneroster_options.unique_id_field) {
          errors.push('OneRoster Unique ID field is required')
        }
        if (!this.lambdaInput.provider_config.api_path_prefix) {
          errors.push('OneRoster API Version is required')
        }
      }
    },

    isValidUrl(url) {
      try {
        new URL(url)
        return true
      } catch (e) {
        return false
      }
    },

    normalizeS3Prefix(prefix) {
      if (!prefix) return ''
      return prefix
        .trim() // Remove leading and trailing whitespace
        .replace(/^\/+/, '') // Remove leading slashes
        .replace(/\/+$/, '') // Remove trailing slashes
    },

    saveConfig() {
      this.loading = true
      this.error = null

      // Set the domain UUID in the lambda input
      this.lambdaInput.import_domain_uuid = this.domainUuid

      // Use the user-selected values for import options
      this.lambdaInput.import_options.autocommit = this.autocommit
      this.lambdaInput.import_options.deactivate_missing = this.deactivateMissing
      this.lambdaInput.import_options.autoupdate_titles = this.autoupdateTitles

      // Ensure district name is set to domain name for Clever
      if (this.lambdaInput.roster_service === ROSTER_SERVICE_CLEVER) {
        if (!this.lambdaInput.clever_config) {
          this.lambdaInput.clever_config = {}
        }
        this.lambdaInput.clever_config.district_name = this.domainName
        this.lambdaInput.clever_config.endpoint = 'https://api.clever.com/v3.0'

        // Process district admin emails
        if (!this.lambdaInput.clever_options) {
          this.lambdaInput.clever_options = {}
        }

        if (this.districtAdminsInclusionType === 'all') {
          this.lambdaInput.clever_options.include_district_admins = true
          this.lambdaInput.clever_options.district_admin_emails_to_include = []
        } else {
          this.lambdaInput.clever_options.include_district_admins = false
          if (this.districtAdminsInclusionType === 'specific') {
            const emails = this.districtAdminsText
              .split('\n')
              .map(email => email.trim())
              .filter(email => email.length > 0)
            this.lambdaInput.clever_options.district_admin_emails_to_include = emails
          } else {
            this.lambdaInput.clever_options.district_admin_emails_to_include = []
          }
        }
      }

      // Ensure Oneroster config has the correct values
      if (this.lambdaInput.roster_service === ROSTER_SERVICE_ONEROSTER) {
        if (!this.lambdaInput.provider_config) {
          this.lambdaInput.provider_config = {
            api_path_prefix: this.oneRosterApiV2
          }
        }
        this.lambdaInput.provider_config.provider_name = this.domainName

        if (!this.lambdaInput.oneroster_options) {
          this.lambdaInput.oneroster_options = {
            unique_id_field: 'sourcedId',
            metadata_title_field: ''
          }
        }
      }

      // Handle S3 CSV specific logic
      if (this.lambdaInput.roster_service === ROSTER_SERVICE_S3CSV) {
        this.lambdaInput.s3_csv_config.domain_name = this.domainName
        // Normalize S3 Input Directory
        if (this.lambdaInput.s3_csv_config.s3_prefix) {
          this.lambdaInput.s3_csv_config.s3_prefix = this.normalizeS3Prefix(
            this.lambdaInput.s3_csv_config.s3_prefix
          )
        }

        // Clear field mappings if not customizing
        if (!this.customizeFieldMappings) {
          this.lambdaInput.s3_csv_options = {
            ...this.lambdaInput.s3_csv_options,
            field_mappings: { ...EMPTY_FIELD_MAPPINGS }
          }
        }
      }

      // Prepare the final config data
      const finalConfig = {
        domain_uuid: this.domainUuid,
        lambda_input: this.lambdaInput
      }

      console.log('Saving config:', finalConfig)
      this.$angularWorld.adminApi
        .saveAutoprovisioningConfig(this.domainUuid, finalConfig)
        .then(config => {
          this.loading = false
          if (this.isEdit) {
            this.$emit('configUpdated', config)
          } else {
            this.$emit('configCreated', config)
          }
        })
        .catch(error => {
          console.error(
            `Error ${this.isEdit ? 'updating' : 'creating'} autoprovisioning config:`,
            error
          )
          this.error =
            typeof error === 'string'
              ? error
              : `Failed to ${this.isEdit ? 'update' : 'create'} configuration`
          this.loading = false
        })
    },
    cancel() {
      this.$emit('cancel')
    },

    initializeFromConfig() {
      if (this.config) {
        // Initialize lambda input from config
        if (this.config.lambda_input) {
          this.lambdaInput = JSON.parse(JSON.stringify(this.config.lambda_input))

          // Set the form values from import options
          if (this.lambdaInput.import_options) {
            this.deactivateMissing = !!this.lambdaInput.import_options.deactivate_missing
            this.autocommit = this.lambdaInput.import_options.autocommit !== false // Default to true if not explicitly set to false
            this.autoupdateTitles = this.lambdaInput.import_options.autoupdate_titles !== false
            this.two_kit = !!this.lambdaInput.import_options.two_kit
          }

          // If the S3 prefix is not set in the existing config, generate it
          if (!this.lambdaInput.csv_upload_s3_prefix) {
            this.generateS3Prefix()
          }

          // Ensure Clever config has the correct values
          if (this.lambdaInput.roster_service === ROSTER_SERVICE_CLEVER) {
            if (!this.lambdaInput.clever_config) {
              this.lambdaInput.clever_config = {}
            }
            this.lambdaInput.clever_config.district_name = this.domainName
            this.lambdaInput.clever_config.endpoint = 'https://api.clever.com/v3.0'

            // Initialize clever_options if needed
            if (!this.lambdaInput.clever_options) {
              this.lambdaInput.clever_options = {
                include_district_admins: false,
                district_admin_emails_to_include: []
              }
            }

            // Determine district admin inclusion type from config
            this.districtAdminsInclusionType = getDistrictAdminsInclusionType(
              this.lambdaInput.clever_options
            )
            if (this.districtAdminsInclusionType === 'specific') {
              this.districtAdminsText = this.lambdaInput.clever_options.district_admin_emails_to_include.join(
                '\n'
              )
            }
          }

          // Ensure Oneroster config has the correct values
          if (this.lambdaInput.roster_service === ROSTER_SERVICE_ONEROSTER) {
            if (!this.lambdaInput.provider_config) {
              this.lambdaInput.provider_config = {
                api_path_prefix: this.oneRosterApiV2
              }
            }
            this.lambdaInput.provider_config.provider_name = this.domainName

            if (!this.lambdaInput.oneroster_options) {
              this.lambdaInput.oneroster_options = {
                unique_id_field: 'sourcedId',
                metadata_title_field: ''
              }
            }
          }

          // Check if S3 CSV field mappings are provided
          const mappings = this.lambdaInput.s3_csv_options?.field_mappings
          if (
            mappings &&
            (mappings.name_field ||
              mappings.email_field ||
              mappings.title_field ||
              mappings.school_field)
          ) {
            this.customizeFieldMappings = true
          }
        }
      } else {
        // If creating a new config, generate the S3 prefix
        this.generateS3Prefix()
      }
    },

    generateS3Prefix() {
      // Get the first two words of the domain name
      const domainWords = this.domainName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters
        .split(/\s+/)
        .slice(0, 2)
        .join('-')

      // Get the first segment of the domain UUID
      const uuidSegment = this.domainUuid.split('-')[0]

      // For all roster services, use auto-generated directory
      this.lambdaInput.csv_upload_s3_prefix = `autoprovision-${domainWords}-${uuidSegment}`
    },
    openTransferFamilyUserModal() {
      if (this.lambdaInput.roster_service !== ROSTER_SERVICE_S3CSV) {
        return
      }

      const username = (this.lambdaInput.s3_csv_config?.s3_prefix || '').trim()

      if (!username) {
        this.transferFamilyUserMode = 'create'
        this.transferFamilyUserFormModel = {
          username: '',
          home_directory: '',
          ssh_public_key: ''
        }
        this.showTransferFamilyModal = true
        return
      }

      this.$angularWorld.adminApi
        .getTransferFamilyUser(this.domainUuid, username)
        .then(user => {
          this.transferFamilyUser = user
          this.transferFamilyUserMode = 'edit'
          this.transferFamilyUserFormModel = {
            username: user.username,
            home_directory: user.home_directory,
            ssh_public_key: user.ssh_public_key || ''
          }
        })
        .catch(() => {
          this.transferFamilyUser = null
          this.transferFamilyUserMode = 'create'
          this.transferFamilyUserFormModel = {
            username,
            home_directory: '',
            ssh_public_key: ''
          }
        })
        .finally(() => {
          this.showTransferFamilyModal = true
        })
    },
    handleTransferFamilyUserSubmit(payload) {
      const mode = this.transferFamilyUserMode

      if (mode === 'edit' && this.transferFamilyUser) {
        const currentUsername = this.transferFamilyUser.username
        this.$angularWorld.adminApi
          .updateTransferFamilyUser(this.domainUuid, currentUsername, payload)
          .then(user => {
            this.transferFamilyUser = user
            this.showTransferFamilyModal = false
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error('Error updating Transfer Family user:', error)
            this.error = 'Failed to edit SFTP user. Please try again.'
          })
      } else {
        this.$angularWorld.adminApi
          .createTransferFamilyUser(this.domainUuid, payload)
          .then(user => {
            this.transferFamilyUser = user
            this.showTransferFamilyModal = false
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error('Error creating Transfer Family user:', error)
            this.error = 'Failed to create SFTP user. Please try again.'
          })
      }
    },
    deleteTransferFamilyUser() {
      if (!this.transferFamilyUser) {
        return
      }

      if (globalThis.confirm(
        'Are you sure you want to delete this SFTP user?'
      )) {
        const username = this.transferFamilyUser.username
        this.$angularWorld.adminApi
          .deleteTransferFamilyUser(this.domainUuid, username)
          .then(() => {
            this.transferFamilyUser = null
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error('Error deleting Transfer Family user:', error)
            this.error = 'Failed to delete SFTP user. Please try again.'
          })
      }
    }
  },
  created() {
    this.ROSTER_SERVICE_CLEVER = ROSTER_SERVICE_CLEVER
    this.ROSTER_SERVICE_ONEROSTER = ROSTER_SERVICE_ONEROSTER
    this.ROSTER_SERVICE_S3CSV = ROSTER_SERVICE_S3CSV

    // Initialize with default values
    this.lambdaInput.import_domain_uuid = this.domainUuid

    // Set default values for Clever config
    if (!this.lambdaInput.clever_config) {
      this.lambdaInput.clever_config = {}
    }
    this.lambdaInput.clever_config.district_name = this.domainName
    this.lambdaInput.clever_config.endpoint = 'https://api.clever.com/v3.0'

    // Initialize clever_options if needed
    if (!this.lambdaInput.clever_options) {
      this.lambdaInput.clever_options = {
        include_district_admins: false,
        district_admin_emails_to_include: []
      }
    }

    // Initialize provider_config for OneRoster
    if (!this.lambdaInput.provider_config) {
      this.lambdaInput.provider_config = {
        provider_name: this.domainName,
        endpoint: '',
        client_id: '',
        client_secret: '',
        api_path_prefix: this.oneRosterApiV2
      }
    }

    // Initialize oneroster_options if needed
    if (!this.lambdaInput.oneroster_options) {
      this.lambdaInput.oneroster_options = {
        unique_id_field: 'sourcedId',
        metadata_title_field: ''
      }
    }

    // If editing, initialize from the provided config, otherwise generate the S3 prefix
    if (this.isEdit) {
      this.initializeFromConfig()
    } else {
      this.generateS3Prefix()
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}
.control-label {
  font-weight: bold;
  margin-bottom: 0;
}
.panel {
  margin-bottom: 20px;
}
.radio-option-first {
  margin-right: 20px;
}
</style>
